import * as React from 'react'
import Link, { LinkProps } from 'next/link'
import { cva, type VariantProps } from 'class-variance-authority'
import { ArrowRight, ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'

// Sharp corners are the brand rule (globals.css zeroes border-radius
// globally). The old `shape` variant (pill/rounded/sharp/...) was dead code
// that never changed the render — removed so the API tells the truth.
const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-none font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-5 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        // Primary is amber (2026-08-08). Magenta had spread across
        // headlines, eyebrows, accent bands, arrows, and indexes, so it no
        // longer read as "act now" — the CTA needed the colour nothing else
        // on the page uses. Every secondary now hovers INTO magenta rather
        // than amber, so a hover state can never look like a resting primary.
        default:
          'shadow hover:scale-105 active:scale-95' +
          ' bg-[var(--accent)] text-[var(--accent-foreground)] hover:bg-[var(--cta-fill)] hover:text-white',
        action:
          'relative bg-[var(--accent)] text-[var(--accent-foreground)] font-semibold' +
          ' hover:translate-y-[-3px] hover:shadow-[0_6px_20px_var(--accent-glow)]' +
          ' active:translate-y-[-1px] transition-all duration-150',
        destructive:
          'bg-[var(--cta-fill)] text-white shadow-sm hover:bg-[var(--cta)] hover:scale-105 active:scale-95',
        outline:
          'border-2 border-[var(--cta)] bg-transparent text-[var(--cta)] shadow-sm hover:bg-[var(--cta-fill)] hover:text-white hover:border-[var(--cta-fill)] hover:scale-105 active:scale-95',
        // Quiet secondary: foreground text on a hairline gray border, for
        // placements where the pink outline would compete with the primary.
        neutral:
          'border-2 border-border/50 bg-transparent text-foreground hover:bg-[var(--cta-fill)] hover:text-white hover:border-[var(--cta-fill)] hover:scale-105 active:scale-95',
        secondary:
          'bg-[color-mix(in_srgb,var(--cta)_20%,transparent)] text-[var(--cta)] shadow-sm hover:bg-[var(--cta-fill)] hover:text-white hover:scale-105 active:scale-95',
        ghost:
          'text-[var(--cta)] hover:bg-[var(--cta-fill)] hover:text-white hover:scale-105 active:scale-95',
        link: 'text-primary underline-offset-4 hover:text-primary/80 hover:underline',
      },
      size: {
        default: 'h-11 px-5 text-base', // 44px height (was 36px)
        sm: 'h-10 px-4 text-sm', // 40px height (was 32px)
        lg: 'h-12 px-10 text-base', // 48px height (was 40px)
        icon: 'h-11 w-11', // 44px × 44px (was 36px)
        link: 'h-auto px-0 py-0 text-base', // Text-style links align with surrounding copy
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
)

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  loading?: boolean
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
}

// Link Button Props
export interface LinkButtonProps
  extends LinkProps, Omit<VariantProps<typeof buttonVariants>, 'asChild'> {
  className?: string
  children: React.ReactNode
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
  style?: React.CSSProperties
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      loading = false,
      leftIcon,
      rightIcon,
      children,
      disabled,
      ...props
    },
    ref
  ) => {
    const resolvedSize = size ?? (variant === 'link' ? 'link' : undefined)

    const content = loading ? (
      <>
        <svg
          className='animate-spin -ml-1 mr-2 h-5 w-5'
          xmlns='http://www.w3.org/2000/svg'
          fill='none'
          viewBox='0 0 24 24'
        >
          <circle
            className='opacity-25'
            cx='12'
            cy='12'
            r='10'
            stroke='currentColor'
            strokeWidth='4'
          />
          <path
            className='opacity-75'
            fill='currentColor'
            d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
          />
        </svg>
        Loading...
      </>
    ) : (
      <>
        {leftIcon && <span className='flex items-center'>{leftIcon}</span>}
        {children}
        {rightIcon && <span className='flex items-center'>{rightIcon}</span>}
      </>
    )

    return (
      <button
        className={cn(
          buttonVariants({ variant, size: resolvedSize, className })
        )}
        disabled={disabled || loading}
        ref={ref}
        {...props}
      >
        {content}
      </button>
    )
  }
)
Button.displayName = 'Button'

// Link Button Component
const LinkButton = React.forwardRef<HTMLAnchorElement, LinkButtonProps>(
  (
    {
      className,
      variant,
      size,
      href,
      children,
      leftIcon,
      rightIcon,
      style,
      ...props
    },
    ref
  ) => {
    const resolvedSize = size ?? (variant === 'link' ? 'link' : undefined)

    const content = (
      <>
        {leftIcon && <span className='flex items-center'>{leftIcon}</span>}
        {children}
        {rightIcon && <span className='flex items-center'>{rightIcon}</span>}
      </>
    )

    return (
      <Link
        className={cn(
          buttonVariants({ variant, size: resolvedSize, className })
        )}
        href={href}
        ref={ref}
        style={style}
        {...props}
      >
        {content}
      </Link>
    )
  }
)
LinkButton.displayName = 'LinkButton'

// Link Button with Arrow Component - FIXED VERSION
export interface LinkButtonWithArrowProps extends Omit<
  LinkButtonProps,
  'rightIcon'
> {
  arrowIcon?: 'chevron' | 'arrow'
  showArrow?: boolean
}

const LinkButtonWithArrow = React.forwardRef<
  HTMLAnchorElement,
  LinkButtonWithArrowProps
>(
  (
    {
      className,
      variant = 'link',
      size,
      href,
      children,
      leftIcon,
      arrowIcon = 'chevron',
      showArrow = true,
      ...props
    },
    ref
  ) => {
    const resolvedSize = size ?? (variant === 'link' ? 'link' : undefined)

    const rightIcon = showArrow ? (
      arrowIcon === 'arrow' ? (
        <ArrowRight size={20} aria-hidden='true' />
      ) : (
        <ChevronRight size={20} aria-hidden='true' />
      )
    ) : null

    // FIXED: Simplified content structure to remove extra wrapper and padding
    const content = (
      <>
        {leftIcon && <span className='flex items-center'>{leftIcon}</span>}
        {children}
        {rightIcon && (
          <span className='inline-block ml-1 transition-transform group-hover:translate-x-1'>
            {rightIcon}
          </span>
        )}
      </>
    )

    return (
      <Link
        className={cn(
          buttonVariants({ variant, size: resolvedSize, className }),
          'group'
        )}
        href={href}
        ref={ref}
        {...props}
      >
        {content}
      </Link>
    )
  }
)
LinkButtonWithArrow.displayName = 'LinkButtonWithArrow'

// Text Button with Arrow (non-link version)
export interface TextButtonWithArrowProps extends Omit<
  ButtonProps,
  'rightIcon'
> {
  arrowIcon?: 'chevron' | 'arrow'
  showArrow?: boolean
}

const TextButtonWithArrow = React.forwardRef<
  HTMLButtonElement,
  TextButtonWithArrowProps
>(
  (
    {
      className,
      variant = 'link',
      size,
      children,
      leftIcon,
      arrowIcon = 'chevron',
      showArrow = true,
      loading = false,
      disabled,
      ...props
    },
    ref
  ) => {
    const resolvedSize = size ?? (variant === 'link' ? 'link' : undefined)

    const rightIcon =
      showArrow && !loading ? (
        arrowIcon === 'arrow' ? (
          <ArrowRight size={20} aria-hidden='true' />
        ) : (
          <ChevronRight size={20} aria-hidden='true' />
        )
      ) : null

    const content = loading ? (
      <>
        <svg
          className='animate-spin -ml-1 mr-2 h-5 w-5'
          xmlns='http://www.w3.org/2000/svg'
          fill='none'
          viewBox='0 0 24 24'
        >
          <circle
            className='opacity-25'
            cx='12'
            cy='12'
            r='10'
            stroke='currentColor'
            strokeWidth='4'
          />
          <path
            className='opacity-75'
            fill='currentColor'
            d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
          />
        </svg>
        Loading...
      </>
    ) : (
      <>
        {leftIcon && <span className='flex items-center'>{leftIcon}</span>}
        <span className='inline-flex items-center gap-1 group'>
          {children}
          {rightIcon && (
            <span className='inline-block transition-transform group-hover:translate-x-1'>
              {rightIcon}
            </span>
          )}
        </span>
      </>
    )

    return (
      <button
        className={cn(
          buttonVariants({ variant, size: resolvedSize, className }),
          'group'
        )}
        disabled={disabled || loading}
        ref={ref}
        {...props}
      >
        {content}
      </button>
    )
  }
)
TextButtonWithArrow.displayName = 'TextButtonWithArrow'

export {
  Button,
  LinkButton,
  LinkButtonWithArrow,
  TextButtonWithArrow,
  buttonVariants,
}
